<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '🖨️ Apa itu Printer Engine?',
    icon: 'ri-printer-line',
    color: 'info',
    content: `
      <p class="mb-4">
        <strong>Printer Engine</strong> adalah pusat pengaturan semua printer yang
        dipakai toko Anda — baik printer struk untuk pelanggan, maupun printer dapur
        untuk memberi tahu barista/koki pesanan apa yang harus dibuat.
      </p>

      <VAlert type="info" variant="tonal" class="mb-4">
        <p class="mb-2">Bayangkan seperti ini:</p>
        <ul class="mb-0">
          <li>Kasir menekan tombol <strong>"Bayar"</strong></li>
          <li>Sistem otomatis mencetak <strong>struk</strong> di printer kasir</li>
          <li>Bersamaan, sistem juga mencetak <strong>tiket dapur</strong> di printer dapur/bar</li>
          <li>Semua ini terjadi otomatis tanpa kasir perlu klik apa-apa lagi</li>
        </ul>
      </VAlert>

      <p class="mb-4">
        Yang membuat fitur ini istimewa: PintuKasir mendukung printer
        <strong>Bluetooth</strong> (nirkabel, tanpa kabel), printer <strong>jaringan/WiFi</strong>,
        dan printer <strong>USB</strong>. Anda bisa punya lebih dari satu printer sekaligus —
        misalnya 1 printer struk di kasir, 1 printer dapur, dan 1 printer bar.
      </p>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Kenapa Harus Didaftarkan Dulu?</h6>
      <p class="mb-0">
        Sebelum printer bisa dipakai untuk mencetak, Anda perlu <strong>mendaftarkannya</strong>
        satu kali di menu ini. Setelah didaftarkan, sistem akan mengingat printer tersebut
        dan otomatis menyambungkannya kembali setiap kali Anda membuka aplikasi.
      </p>
    `,
  },

  {
    title: '➕ Cara Menambahkan Printer Bluetooth (Pairing)',
    icon: 'ri-bluetooth-line',
    color: 'success',
    content: `
      <p class="mb-4">
        Ikuti langkah ini jika printer Anda adalah printer thermal Bluetooth
        (biasanya kecil, portable, dan mengisi baterai — umum dipakai di coffee shop kecil).
      </p>

      <VAlert type="warning" variant="tonal" class="mb-4">
        <strong>Sebelum mulai:</strong> Pastikan printer sudah <strong>menyala</strong> dan
        <strong>Bluetooth di HP/laptop/tablet kasir sudah aktif</strong>. Gunakan browser
        <strong>Google Chrome</strong> — fitur ini belum tentu jalan di browser lain.
      </VAlert>

      <ol class="ps-4">
        <li class="mb-3">
          Buka menu <strong>Pengaturan → Printer</strong> dari sidebar kiri
        </li>
        <li class="mb-3">
          Klik tombol <strong>"Pairing Bluetooth"</strong> di pojok kanan atas
        </li>
        <li class="mb-3">
          Isi <strong>Nama Printer</strong> agar mudah dikenali, contoh: <em>"Printer Kasir Depan"</em>
        </li>
        <li class="mb-3">
          Pilih <strong>Lebar Kertas</strong> sesuai printer Anda: <strong>58mm</strong>
          (paling umum, kertas kecil) atau <strong>80mm</strong> (kertas lebih lebar)
        </li>
        <li class="mb-3">
          (Opsional) Pilih <strong>Stasiun</strong> jika printer ini khusus untuk dapur/bar tertentu
        </li>
        <li class="mb-3">
          Klik <strong>"Mulai Pairing"</strong> — browser akan menampilkan daftar semua
          device Bluetooth yang terdeteksi di sekitar Anda
        </li>
        <li class="mb-0">
          Pilih nama printer Anda dari daftar tersebut, lalu klik <strong>"Pair"</strong>.
          Printer akan otomatis tersimpan dan siap dipakai ✅
        </li>
      </ol>

      <VAlert type="error" variant="tonal" class="mt-4">
        <strong>Muncul pesan "Browser ini tidak mendukung Web Bluetooth"?</strong><br>
        Artinya HP/laptop/browser yang Anda pakai belum mendukung pairing Bluetooth
        langsung dari aplikasi. Solusinya: gunakan Google Chrome di Android atau komputer,
        atau daftarkan printer sebagai <strong>printer jaringan/USB</strong> (lihat bagian berikutnya).
      </VAlert>
    `,
  },

  {
    title: '🔌 Cara Menambahkan Printer Jaringan (WiFi/LAN) atau USB',
    icon: 'ri-router-line',
    color: 'primary',
    content: `
      <p class="mb-4">
        Gunakan cara ini jika printer Anda tersambung lewat kabel USB ke komputer kasir,
        atau lewat jaringan WiFi/LAN kantor (biasa dipakai di restoran/resto yang lebih besar).
      </p>

      <ol class="ps-4 mb-4">
        <li class="mb-3">
          Buka menu <strong>Pengaturan → Printer</strong>
        </li>
        <li class="mb-3">
          Klik tombol <strong>"Tambah Manual"</strong>
        </li>
        <li class="mb-3">
          Isi <strong>Nama Printer</strong>, contoh: <em>"Printer Dapur Utama"</em>
        </li>
        <li class="mb-3">
          Pilih <strong>Tipe</strong>: <strong>Jaringan (LAN)</strong> jika lewat WiFi,
          atau <strong>USB</strong> jika lewat kabel
        </li>
        <li class="mb-3">
          Isi kolom alamat sesuai tipe:
          <ul class="mt-2">
            <li>Untuk <strong>Jaringan</strong>: isi <strong>Alamat IP dan Port</strong> printer,
              contoh <code>192.168.1.50:9100</code>. Alamat ini biasanya bisa dilihat dari
              menu setting di layar printer itu sendiri, atau tanya teknisi yang memasang jaringan toko Anda.</li>
            <li>Untuk <strong>USB</strong>: isi nama/path device sesuai petunjuk dari komputer kasir Anda.</li>
          </ul>
        </li>
        <li class="mb-3">
          Pilih <strong>Lebar Kertas</strong> (58mm atau 80mm)
        </li>
        <li class="mb-0">
          Klik <strong>"Simpan"</strong>
        </li>
      </ol>

      <VAlert type="info" variant="tonal">
        <strong>Tidak tahu alamat IP printer Anda?</strong> Biasanya bisa dicetak lewat
        tombol "test print" di printer itu sendiri, atau tanyakan ke toko/teknisi tempat
        Anda membeli printer tersebut.
      </VAlert>
    `,
  },

  {
    title: '📋 Penjelasan Setiap Kolom di Form Printer',
    icon: 'ri-list-check',
    color: 'secondary',
    content: `
      <p class="mb-4">
        Berikut penjelasan lengkap setiap kolom yang ada saat menambah atau mengedit data printer.
      </p>

      <VTable class="mb-4">
        <thead>
          <tr>
            <th style="min-width:140px">Nama Kolom</th>
            <th style="width:80px">Wajib?</th>
            <th>Penjelasan</th>
            <th style="min-width:160px">Contoh</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Nama Printer</strong></td>
            <td><VChip size="x-small" color="error" variant="tonal">Wajib</VChip></td>
            <td>Nama yang memudahkan Anda mengenali printer, terutama jika punya lebih dari satu.</td>
            <td class="text-caption">Printer Kasir Depan, Printer Dapur, Printer Bar</td>
          </tr>
          <tr>
            <td><strong>Tipe</strong></td>
            <td><VChip size="x-small" color="error" variant="tonal">Wajib</VChip></td>
            <td>
              Jenis koneksi printer. <strong>Bluetooth</strong> untuk printer nirkabel portable,
              <strong>Jaringan (LAN)</strong> untuk printer WiFi, <strong>USB</strong> untuk printer kabel.
            </td>
            <td class="text-caption">Bluetooth, Jaringan (LAN), USB</td>
          </tr>
          <tr>
            <td><strong>Lebar Kertas</strong></td>
            <td><VChip size="x-small" color="error" variant="tonal">Wajib</VChip></td>
            <td>
              Ukuran kertas yang dipakai printer Anda. Pilih salah yang tidak sesuai akan membuat
              hasil cetak terpotong atau tidak rapi.
            </td>
            <td class="text-caption">58mm (umum), 80mm (lebih lebar)</td>
          </tr>
          <tr>
            <td><strong>Stasiun</strong></td>
            <td><VChip size="x-small" color="success" variant="tonal">Opsional</VChip></td>
            <td>
              Menentukan printer ini khusus mencetak tiket untuk stasiun dapur/bar yang mana.
              Kosongkan jika printer ini hanya untuk struk kasir.
            </td>
            <td class="text-caption">Dapur Panas, Bar Minuman</td>
          </tr>
          <tr>
            <td><strong>Default Struk</strong></td>
            <td><VChip size="x-small" color="success" variant="tonal">Opsional</VChip></td>
            <td>
              Tandai satu printer sebagai printer utama untuk mencetak struk pelanggan.
              Hanya boleh satu printer yang jadi default struk dalam satu waktu.
            </td>
            <td class="text-caption">Aktif untuk printer kasir utama</td>
          </tr>
          <tr>
            <td><strong>Status Aktif</strong></td>
            <td><VChip size="x-small" color="success" variant="tonal">Opsional</VChip></td>
            <td>
              Printer yang nonaktif tidak akan dipakai untuk mencetak apapun,
              tapi datanya tetap tersimpan. Gunakan ini saat printer rusak/sedang diservis.
            </td>
            <td class="text-caption">Aktif (default)</td>
          </tr>
        </tbody>
      </VTable>
    `,
  },

  {
    title: '⭐ Cara Mengatur Printer Default & Menyambungkan ke Stasiun',
    icon: 'ri-star-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        Jika toko Anda punya lebih dari satu printer, Anda perlu menentukan
        printer mana yang jadi "utama" untuk struk, dan printer mana yang
        melayani stasiun dapur/bar tertentu.
      </p>

      <h6 class="text-subtitle-1 font-weight-bold mb-2">Menjadikan Printer Default untuk Struk</h6>
      <ol class="ps-4 mb-4">
        <li class="mb-2">Buka daftar printer di menu <strong>Pengaturan → Printer</strong></li>
        <li class="mb-2">Cari printer yang ingin dijadikan default</li>
        <li class="mb-0">Klik tautan <strong>"Jadikan Default"</strong> di kolom Default Struk</li>
      </ol>

      <h6 class="text-subtitle-1 font-weight-bold mb-2">Menyambungkan Printer ke Stasiun Dapur</h6>
      <ol class="ps-4">
        <li class="mb-2">Klik ikon <strong>pensil (✏️)</strong> pada printer yang ingin diatur</li>
        <li class="mb-2">
          Pilih <strong>Stasiun</strong> yang sesuai, misalnya "Dapur Panas" untuk printer
          yang ada di area masak, atau "Bar Minuman" untuk printer di area minuman
        </li>
        <li class="mb-0">Klik <strong>"Simpan"</strong></li>
      </ol>

      <VAlert type="info" variant="tonal" class="mt-4">
        Fitur ini bekerja bersama menu <strong>Stasiun Dapur</strong> — pastikan Anda
        sudah membuat data stasiun terlebih dahulu sebelum menyambungkannya ke printer.
      </VAlert>
    `,
  },

  {
    title: '📦 Antrian Cetak — Kenapa Struk Kadang Tertunda?',
    icon: 'ri-list-ordered',
    color: 'info',
    content: `
      <p class="mb-4">
        PintuKasir dirancang agar kasir tetap bisa jalan meskipun printer sedang
        tidak menyala, sedang di luar jangkauan Bluetooth, atau internet toko sedang lambat.
        Caranya: setiap perintah cetak masuk dulu ke <strong>Antrian Cetak</strong>
        sebelum benar-benar dikirim ke printer.
      </p>

      <VAlert type="info" variant="tonal" class="mb-4">
        <p class="mb-2">Contoh skenario:</p>
        <ul class="mb-0">
          <li>Kasir menekan "Bayar" — struk otomatis masuk ke antrian</li>
          <li>Ternyata printer Bluetooth sedang mati/di luar jangkauan</li>
          <li>Sistem menampilkan status <strong>"Menunggu printer"</strong> — bukan error, hanya menunggu</li>
          <li>Begitu printer dinyalakan lagi, sistem otomatis menyambung dan mencetak struk yang tertunda</li>
        </ul>
      </VAlert>

      <p class="mb-4">
        Anda bisa melihat semua job yang sedang menunggu atau gagal di bagian
        <strong>"Antrian Cetak"</strong> pada halaman ini. Setiap job menunjukkan:
      </p>

      <VTable class="mb-4">
        <thead>
          <tr>
            <th style="min-width:140px">Status</th>
            <th>Artinya</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><VChip size="small" color="warning" variant="tonal">Menunggu printer</VChip></td>
            <td>Struk/tiket sudah masuk antrian, tapi printer belum tersambung. Tidak perlu diulang manual — begitu printer nyala, otomatis lanjut cetak.</td>
          </tr>
          <tr>
            <td><VChip size="small" color="info" variant="tonal">pending</VChip></td>
            <td>Sedang menunggu giliran untuk dikirim ke printer yang sudah tersambung.</td>
          </tr>
          <tr>
            <td><VChip size="small" color="success" variant="tonal">success</VChip></td>
            <td>Sudah berhasil dicetak. Job ini akan hilang otomatis dari daftar antrian setelah sukses.</td>
          </tr>
          <tr>
            <td><VChip size="small" color="error" variant="tonal">failed</VChip></td>
            <td>Sistem sudah mencoba mencetak 3 kali dan tetap gagal (biasanya karena printer kehabisan kertas, rusak, atau memang tidak pernah tersambung). Ada tombol <strong>"Coba Lagi"</strong> untuk mengulang setelah masalah diperbaiki.</td>
          </tr>
        </tbody>
      </VTable>

      <VAlert type="success" variant="tonal">
        <strong>Aman ditinggal reload/refresh halaman.</strong> Antrian cetak tersimpan
        di perangkat kasir, jadi meskipun Anda menutup lalu membuka lagi aplikasinya,
        job yang belum tercetak tidak akan hilang.
      </VAlert>
    `,
  },

  {
    title: '🔄 Sambung Ulang Otomatis (Auto-Reconnect)',
    icon: 'ri-refresh-line',
    color: 'success',
    content: `
      <p class="mb-4">
        Khusus printer Bluetooth, Anda <strong>tidak perlu pairing ulang setiap hari</strong>.
        Setelah pairing pertama kali berhasil, PintuKasir akan mencoba menyambungkan
        kembali printer tersebut secara otomatis setiap kali:
      </p>

      <ul class="mb-4">
        <li>Aplikasi dibuka ulang / halaman di-refresh</li>
        <li>Printer sempat mati lalu dinyalakan kembali</li>
        <li>Printer sempat keluar jangkauan Bluetooth lalu kembali dekat</li>
      </ul>

      <VAlert type="warning" variant="tonal" class="mb-4">
        <strong>Catatan:</strong> Fitur sambung otomatis ini hanya bekerja penuh di
        <strong>Google Chrome</strong>. Browser lain mungkin tetap meminta Anda memilih
        ulang printer dari daftar Bluetooth setelah aplikasi ditutup.
      </VAlert>

      <p class="mb-0">
        Jika printer tidak kunjung tersambung otomatis setelah beberapa saat,
        coba matikan-nyalakan printer, pastikan baterainya cukup, lalu buka kembali
        halaman <strong>Pengaturan → Printer</strong>.
      </p>
    `,
  },

  {
    title: '⚠️ Peringatan & Batasan Penting',
    icon: 'ri-alert-line',
    color: 'error',
    content: `
      <VAlert type="error" variant="tonal" class="mb-4">
        <strong>Hanya boleh ada satu printer default struk!</strong><br>
        Saat Anda menjadikan sebuah printer sebagai default struk baru, printer default
        yang lama otomatis tidak lagi jadi default. Pastikan printer yang benar-benar
        aktif di kasir yang dijadikan default.
      </VAlert>

      <VAlert type="warning" variant="tonal" class="mb-4">
        <strong>Pairing Bluetooth wajib dilakukan langsung dari perangkat kasir!</strong><br>
        Karena Bluetooth menyambungkan perangkat secara fisik, pairing harus dilakukan
        dari HP/tablet/laptop yang benar-benar dipakai di kasir tersebut, bukan dari
        perangkat lain.
      </VAlert>

      <VAlert type="info" variant="tonal" class="mb-4">
        <strong>Menghapus data printer tidak menghapus riwayat cetak!</strong><br>
        Semua struk dan tiket yang pernah tercetak tetap tersimpan di riwayat transaksi,
        meskipun printernya sudah dihapus dari daftar.
      </VAlert>

      <VAlert type="warning" variant="tonal">
        <strong>Job yang gagal 3 kali tidak dicoba otomatis lagi.</strong><br>
        Setelah 3 kali percobaan gagal, job akan berstatus <strong>failed</strong> dan
        berhenti dicoba otomatis. Anda perlu klik tombol <strong>"Coba Lagi"</strong>
        secara manual setelah masalah printer diperbaiki.
      </VAlert>
    `,
  },

  {
    title: '💡 Tips & Best Practices',
    icon: 'ri-lightbulb-line',
    color: 'warning',
    content: `
      <VRow>
        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-4 h-100">
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VIcon icon="ri-battery-charge-line" color="warning" class="me-2" size="24" />
                <strong>Jaga Baterai Printer Bluetooth</strong>
              </div>
              <p class="text-body-2 mb-0">
                Pastikan printer thermal Bluetooth selalu terisi/tercolok charger saat jam
                operasional. Printer yang mati mendadak akan membuat semua struk masuk
                antrian "Menunggu printer" sampai dinyalakan kembali.
              </p>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-4 h-100">
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VIcon icon="ri-map-pin-line" color="info" class="me-2" size="24" />
                <strong>Dekatkan Printer ke Kasir</strong>
              </div>
              <p class="text-body-2 mb-0">
                Jarak Bluetooth yang ideal biasanya di bawah 5-10 meter tanpa halangan
                tembok. Semakin dekat printer ke perangkat kasir, semakin stabil koneksinya.
              </p>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-4 h-100">
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VIcon icon="ri-restaurant-2-line" color="success" class="me-2" size="24" />
                <strong>Satu Printer per Stasiun</strong>
              </div>
              <p class="text-body-2 mb-0">
                Untuk resto dengan dapur besar, sebaiknya pisahkan printer untuk tiap
                stasiun (dapur panas, dapur dingin, bar). Ini membuat tiket lebih cepat
                sampai ke orang yang tepat, tanpa perlu memilah-milah kertas.
              </p>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-4 h-100">
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VIcon icon="ri-checkbox-circle-line" color="primary" class="me-2" size="24" />
                <strong>Cek Antrian Cetak Setiap Pagi</strong>
              </div>
              <p class="text-body-2 mb-0">
                Sebelum toko buka, cek halaman ini sebentar untuk memastikan tidak ada
                job "failed" tersisa dari hari sebelumnya yang perlu di-"Coba Lagi".
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    `,
  },

  {
    title: '❓ Pertanyaan yang Sering Diajukan (FAQ)',
    icon: 'ri-question-answer-line',
    color: 'primary',
    content: `
      <VExpansionPanels variant="accordion">
        <VExpansionPanel>
          <VExpansionPanelTitle>
            Struk tidak keluar sama sekali, apa yang harus saya cek?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p class="mb-2">Cek berurutan berikut ini:</p>
            <ol class="mb-0">
              <li>Pastikan printer menyala dan ada kertas</li>
              <li>Cek halaman Antrian Cetak — apakah statusnya "Menunggu printer" atau "failed"?</li>
              <li>Untuk printer Bluetooth, pastikan jaraknya dekat dan baterai cukup</li>
              <li>Untuk printer jaringan, pastikan WiFi toko tidak sedang bermasalah</li>
              <li>Jika masih gagal, coba klik "Coba Lagi" pada job yang berstatus failed</li>
            </ol>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Apakah saya perlu pairing ulang setiap hari?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p class="mb-0">
              Tidak, untuk printer Bluetooth yang dipakai dengan Google Chrome, sistem akan
              mencoba menyambungkan kembali secara otomatis. Pairing manual hanya diperlukan
              sekali di awal, atau jika Anda pindah ke perangkat kasir baru.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Bisakah satu toko punya lebih dari satu printer struk?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p class="mb-0">
              Bisa. Anda bisa daftarkan berapa pun printer, tapi hanya satu yang boleh
              ditandai sebagai <strong>"Default Struk"</strong> — yaitu printer yang
              otomatis dipakai saat kasir menyelesaikan transaksi.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Hasil cetak terpotong/tidak rapi, kenapa?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p class="mb-0">
              Kemungkinan besar pengaturan <strong>Lebar Kertas</strong> pada printer tidak
              sesuai dengan kertas yang benar-benar dipakai. Buka menu edit printer,
              lalu sesuaikan ke 58mm atau 80mm sesuai kertas fisiknya.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Kalau internet toko mati, apakah struk tetap bisa cetak?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p class="mb-0">
              Proses cetak ke printer Bluetooth/USB tidak butuh internet — koneksinya
              langsung dari perangkat kasir ke printer. Namun, laporan status cetak ke
              server pusat baru akan tersinkron begitu internet kembali normal.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Bisakah saya menghapus printer yang sudah tidak dipakai?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p class="mb-0">
              Bisa. Klik ikon tempat sampah pada printer tersebut lalu konfirmasi hapus.
              Riwayat struk/tiket yang pernah dicetak lewat printer itu tetap aman tersimpan.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>
    `,
  },
]
</script>

<template>
  <div>
    <!-- Back Button -->
    <VRow>
      <VCol cols="12">
        <RouterLink
          to="/userguide"
          class="text-decoration-none"
        >
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            class="mb-2 px-0"
          >
            Kembali ke Sistem
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow class="mb-2">
      <VCol cols="12">
        <div class="d-flex align-center flex-wrap gap-4 mb-4">
          <VAvatar
            color="info"
            variant="tonal"
            size="72"
            rounded="lg"
          >
            <VIcon
              icon="ri-printer-line"
              size="40"
            />
          </VAvatar>
          <div>
            <div class="text-caption text-medium-emphasis mb-1">
              Modul Sistem
            </div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Printer
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Sambungkan dan kelola semua printer struk & dapur toko Anda
            </p>
          </div>
        </div>

        <!-- Quick Info Bar -->
        <VRow class="mb-2">
          <VCol
            cols="6"
            sm="3"
          >
            <VCard
              variant="tonal"
              color="success"
            >
              <VCardText class="text-center pa-3">
                <VIcon
                  icon="ri-bluetooth-line"
                  size="28"
                  class="mb-1"
                />
                <div class="text-caption">Pairing Bluetooth</div>
              </VCardText>
            </VCard>
          </VCol>
          <VCol
            cols="6"
            sm="3"
          >
            <VCard
              variant="tonal"
              color="primary"
            >
              <VCardText class="text-center pa-3">
                <VIcon
                  icon="ri-router-line"
                  size="28"
                  class="mb-1"
                />
                <div class="text-caption">Printer Jaringan/USB</div>
              </VCardText>
            </VCard>
          </VCol>
          <VCol
            cols="6"
            sm="3"
          >
            <VCard
              variant="tonal"
              color="warning"
            >
              <VCardText class="text-center pa-3">
                <VIcon
                  icon="ri-list-ordered"
                  size="28"
                  class="mb-1"
                />
                <div class="text-caption">Antrian Cetak</div>
              </VCardText>
            </VCard>
          </VCol>
          <VCol
            cols="6"
            sm="3"
          >
            <VCard
              variant="tonal"
              color="info"
            >
              <VCardText class="text-center pa-3">
                <VIcon
                  icon="ri-refresh-line"
                  size="28"
                  class="mb-1"
                />
                <div class="text-caption">Sambung Otomatis</div>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VCol>
    </VRow>

    <!-- Sections -->
    <VRow>
      <VCol cols="12">
        <VExpansionPanels
          variant="accordion"
          multiple
          :model-value="[0, 1]"
        >
          <VExpansionPanel
            v-for="(section, index) in sections"
            :key="index"
          >
            <VExpansionPanelTitle class="py-4">
              <div class="d-flex align-center">
                <VIcon
                  :icon="section.icon"
                  :color="section.color"
                  size="22"
                  class="me-3 flex-shrink-0"
                />
                <span class="font-weight-medium">{{ section.title }}</span>
              </div>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div
                class="text-body-2 pa-2"
                style="line-height: 1.8"
                v-html="section.content"
              />
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related Pages -->
    <VRow class="mt-8">
      <VCol cols="12">
        <h5 class="text-h5 mb-4">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/fb-modern/stations"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="primary"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-restaurant-2-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Stasiun Dapur</div>
                    <div class="text-caption text-medium-emphasis">
                      Atur dapur panas, dapur dingin, bar minuman
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/fb-modern/kds"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="success"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-tv-2-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Kitchen Display</div>
                    <div class="text-caption text-medium-emphasis">
                      Monitor pesanan dapur secara realtime
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/sistem/pengaturan"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="warning"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-settings-3-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Pengaturan Umum</div>
                    <div class="text-caption text-medium-emphasis">
                      Konfigurasi pajak, service charge, & struk
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
